import { useQuery } from '@apollo/client';
import { STUDIOS_FOR_USER_PROFILE } from '../graphql/queries/queries';
import { GET_USER_PROFILE, GET_PUBLIC_USER_PROFILE } from '../graphql/queries/authQueries';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { getCloudFrontImageUrl } from '../util/cloudFrontUtils';
import { filterHostVisibleStudios } from '../util/studioStatus';

export const useProfilePageUser = (userId) => {
  const { user: authenticatedUser } = useSelector((state) => state?.auth || {});
  const authenticatedUserId = authenticatedUser?._id;
  const isOwner = authenticatedUserId && userId && authenticatedUserId === userId;

  const { data, loading, error } = useQuery(STUDIOS_FOR_USER_PROFILE, {
    variables: { 
      userID: userId,
      limit: 100
    },
    skip: !userId,
    fetchPolicy: 'cache-and-network',
  });

  // Fallback query for user profile if no studios are found
  const fallbackProfileQuery = isOwner ? GET_USER_PROFILE : GET_PUBLIC_USER_PROFILE;

  const { data: userProfileData, loading: userProfileLoading, error: userProfileError } = useQuery(fallbackProfileQuery, {
    variables: { id: userId },
    skip: !userId || (data?.studiosByUserID?.items?.length > 0), // Skip if we already have user data from studios
    fetchPolicy: 'cache-and-network',
  });

  // Process all data including image URLs
  const processedData = useMemo(() => {
    const studios = filterHostVisibleStudios(data?.studiosByUserID?.items || []);
    
    // Get user data from studios or fallback profile query
    let userData = null;
    if (studios.length > 0) {
      userData = studios[0]?.User || null;
    } else if (userProfileData?.getUser) {
      userData = userProfileData.getUser;
    }

    // If no studios, return user data only
    if (studios.length === 0) {
      if (!userData) return null;

      // Process user profile picture
      let processedProfilePicture = "";
      if (userData.profilePicture) {
        try {
          processedProfilePicture = getCloudFrontImageUrl(userData.profilePicture, {
            width: 200,
            height: 200,
            fit: 'cover'
          });
        } catch (error) {
          console.error('Error processing user profile picture:', error);
          processedProfilePicture = "";
        }
      }

      return {
        user: {
          ...userData,
          processedProfilePicture
        },
        studios: [],
        hasStudios: false
      };
    }

    const processedStudios = studios.map(studio => {
      const processedStudio = { ...studio };

      // Process studio images
      if (processedStudio.images && Array.isArray(processedStudio.images)) {
        processedStudio.processedImages = processedStudio.images.map(imageKey => {
          if (!imageKey) return '';

          try {
            return getCloudFrontImageUrl(imageKey, {
              width: 1200,
              height: 800,
              fit: 'cover'
            });
          } catch (error) {
            console.error('Error processing studio image:', error);
            return '';
          }
        });
      } else {
        processedStudio.processedImages = [];
        processedStudio.images = [];
      }

      processedStudio.hasImages = processedStudio.images && processedStudio.images.length > 0;

      // Process User data if it exists
      if (processedStudio.User?.profilePicture) {
        let processedProfilePicture = "";

        try {
          processedProfilePicture = getCloudFrontImageUrl(processedStudio.User.profilePicture, {
            width: 200,
            height: 200,
            fit: 'cover'
          });
        } catch (error) {
          console.error('Error processing user profile picture:', error);
          processedProfilePicture = "";
        }

        processedStudio.User = {
          ...processedStudio.User,
          processedProfilePicture
        };
      }

      return processedStudio;
    });

    // Get user data from the first studio (they should all have the same user)  
    const finalUserData = processedStudios[0]?.User || userData;

    return {
      user: finalUserData,
      studios: processedStudios,
      hasStudios: processedStudios.length > 0
    };
  }, [data, userProfileData]);

  return {
    userData: processedData,
    loading: loading || userProfileLoading,
    error: error || userProfileError
  };
};